import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';

// Bloques de contenido para los artículos (nov-2026, diseño B2B): sobrios, rectos,
// filetes finos, sin cajas de colores ni emojis. Van como hijos directos de
// ArticleLayout, entre los párrafos.

/** Aviso o idea clave: filete azul a la izquierda sobre gris muy claro. */
export const Callout = ({ title, children }) => (
  <aside className="border-l-2 border-primary-700 bg-slate-50 px-6 py-5">
    {title && <p className="font-semibold text-slate-900 mb-2">{title}</p>}
    <div className="text-[16px] leading-7 text-slate-700 space-y-3">{children}</div>
  </aside>
);

/** Cifras o datos clave en rejilla. items = [{ value, label, text? }]. */
export const KeyFacts = ({ items, cols = 3 }) => (
  <div className={`grid sm:grid-cols-2 ${cols === 3 ? 'lg:grid-cols-3' : cols === 4 ? 'lg:grid-cols-4' : ''} gap-x-8 gap-y-6 border-t border-slate-300 pt-6`}>
    {items.map((it) => (
      <div key={it.label}>
        <p className="text-2xl font-semibold tracking-tight text-slate-900">{it.value}</p>
        <p className="mt-1 text-sm font-semibold text-slate-700">{it.label}</p>
        {it.text && <p className="mt-1 text-sm leading-6 text-slate-500">{it.text}</p>}
      </div>
    ))}
  </div>
);

/** Puntos con título y texto, con filete. items = [{ title, text }]. */
export const Points = ({ items, cols = 2 }) => (
  <div className={`grid ${cols === 2 ? 'sm:grid-cols-2' : cols === 3 ? 'sm:grid-cols-2 lg:grid-cols-3' : ''} gap-x-8 gap-y-6`}>
    {items.map((it) => (
      <div key={it.title} className="border-t border-slate-300 pt-4">
        <p className="font-semibold text-slate-900">{it.title}</p>
        <div className="mt-1.5 text-[15px] leading-7 text-slate-600">{it.text}</div>
      </div>
    ))}
  </div>
);

/** Lista con marcas de verificación. */
export const Checks = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map((it, i) => (
      <li key={i} className="flex items-start gap-3 text-[16px] leading-7 text-slate-700">
        <Check className="w-4 h-4 mt-1.5 flex-shrink-0 text-primary-700" strokeWidth={2} />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

/** Tabla con filetes finos. head = [..]; rows = [[..], ..]. Las celdas admiten JSX. */
export const DataTable = ({ head, rows, caption }) => (
  <figure className="overflow-x-auto">
    <table className="w-full min-w-[560px] border-collapse text-left text-[15px]">
      <thead>
        <tr className="border-b-2 border-slate-900">
          {head.map((h) => (
            <th key={h} scope="col" className="py-3 pr-6 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 align-bottom">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-slate-200 align-top">
            {row.map((cell, j) => (
              <td key={j} className={`py-3.5 pr-6 leading-6 ${j === 0 ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
    {caption && <figcaption className="mt-3 text-sm text-slate-500">{caption}</figcaption>}
  </figure>
);

/** Cronología: fecha a la izquierda, hito a la derecha. items = [{ date, title, text? }]. */
export const Timeline = ({ items }) => (
  <ol className="border-t border-slate-300">
    {items.map((it) => (
      <li key={`${it.date}-${it.title}`} className="grid sm:grid-cols-4 gap-x-6 gap-y-1 border-b border-slate-200 py-4">
        <p className="text-sm font-semibold text-primary-700">{it.date}</p>
        <div className="sm:col-span-3">
          <p className="font-semibold text-slate-900">{it.title}</p>
          {it.text && <p className="mt-1 text-[15px] leading-7 text-slate-600">{it.text}</p>}
        </div>
      </li>
    ))}
  </ol>
);

/** Pasos numerados. items = [{ title, text }]. */
export const Steps = ({ items }) => (
  <ol className="space-y-5">
    {items.map((it, i) => (
      <li key={it.title} className="grid grid-cols-[2.5rem_1fr] gap-3">
        <span className="text-sm font-semibold text-primary-700 tabular-nums pt-0.5">{String(i + 1).padStart(2, '0')}</span>
        <div>
          <p className="font-semibold text-slate-900">{it.title}</p>
          {it.text && <div className="mt-1 text-[15px] leading-7 text-slate-600">{it.text}</div>}
        </div>
      </li>
    ))}
  </ol>
);

/** Imagen con pie (documentos de ejemplo, capturas). width/height evitan saltos al cargar. */
export const Figure = ({ src, alt, caption, width, height }) => (
  <figure className="!mt-8">
    <div className="border border-slate-200 bg-white shadow-sm">
      <img src={src} alt={alt} width={width} height={height} loading="lazy" className="block w-full h-auto" />
    </div>
    {caption && <figcaption className="mt-3 text-sm leading-6 text-slate-500">{caption}</figcaption>}
  </figure>
);

/** Enlace destacado dentro del artículo (a otra página de la web). */
export const ArticleLink = ({ to, href, title, text }) => {
  const body = (
    <>
      <span>
        <span className="block font-semibold text-slate-900 group-hover:text-primary-700 transition-colors">{title}</span>
        {text && <span className="mt-1 block text-sm leading-6 text-slate-600">{text}</span>}
      </span>
      <ArrowRight className="mt-1 w-5 h-5 flex-shrink-0 text-slate-400 group-hover:text-primary-700 group-hover:translate-x-1 transition" />
    </>
  );
  const cls = 'group flex items-start justify-between gap-6 border-y border-slate-300 py-5';
  return href
    ? <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>{body}</a>
    : <Link to={to} className={cls}>{body}</Link>;
};
